/**
 * Surveys Help Content
 *
 * Covers survey dashboard, creation, taking surveys, and results.
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createWalkthrough,
  createTroubleshooting,
  createRelatedLink,
} from '../utils';

// =============================================================================
// MAIN SURVEYS HELP
// =============================================================================

export const surveysHelp: HelpContent = createHelpContent({
  id: 'surveys',
  title: 'Surveys',
  description: 'Create, manage, and respond to organizational surveys',
  lastUpdated: '2026-02-08',
  version: '2.0',

  tips: [
    createTip(
      'tip-question-types',
      'pro-tip',
      'Choose the Right Question Type',
      'Use rating/NPS for measurable feedback, multiple choice for quick answers, and text for open-ended insights. Mix types for the best response quality.'
    ),
    createTip(
      'tip-anonymous',
      'info',
      'Anonymous Surveys',
      'When a survey is marked anonymous, individual responses cannot be traced back to respondents — even by administrators.'
    ),
  ],

  sections: [
    createSection(
      'overview',
      'Overview',
      `<p>The Surveys section allows you to participate in and manage organizational surveys. Collect feedback, measure engagement, and make data-driven decisions.</p>
      <p>The survey system supports:</p>
      <ul>
        <li><strong>Survey Dashboard</strong> — View pending, active, and completed surveys</li>
        <li><strong>Survey Creation</strong> — Build surveys with multiple question types</li>
        <li><strong>Taking Surveys</strong> — Respond to surveys assigned to you</li>
        <li><strong>Results & Analytics</strong> — Analyze responses and track trends</li>
      </ul>`
    ),
    createSection(
      'taking-surveys',
      'Taking Surveys',
      `<p>When you receive a survey invitation:</p>
      <ul>
        <li>Click the survey link or find it in your pending surveys list</li>
        <li>Read each question carefully before responding</li>
        <li>Your responses may be anonymous depending on the survey settings</li>
        <li>Required questions are marked with an asterisk (*)</li>
        <li>Submit when you've completed all required questions</li>
      </ul>
      <p>Your honest feedback helps improve the organization. Take your time with open-ended questions.</p>`
    ),
    createSection(
      'question-types',
      'Question Types',
      `<p>Surveys can include the following question types:</p>
      <ul>
        <li><strong>Multiple Choice</strong> — Select one option from a list</li>
        <li><strong>Checkbox</strong> — Select multiple options from a list</li>
        <li><strong>Rating</strong> — Rate on a numeric scale (e.g., 1-5 stars)</li>
        <li><strong>NPS (Net Promoter Score)</strong> — Rate on a 0-10 scale, commonly used for satisfaction</li>
        <li><strong>Linear Scale</strong> — Rate on a custom scale with labeled endpoints</li>
        <li><strong>Text</strong> — Open-ended written response</li>
        <li><strong>Matrix</strong> — Rate multiple items on the same scale (rows × columns)</li>
        <li><strong>Ranking</strong> — Drag items into your preferred order</li>
      </ul>`
    ),
    createSection(
      'audience',
      'Audience Targeting',
      `<p>Survey creators can target specific audiences:</p>
      <ul>
        <li><strong>All Employees</strong> — Organization-wide survey</li>
        <li><strong>Specific Teams</strong> — Only members of selected teams</li>
        <li><strong>Specific Departments</strong> — Target by department</li>
        <li><strong>Custom Selection</strong> — Hand-pick individual recipients</li>
      </ul>
      <p>Targeted surveys ensure relevant feedback from the right audience.</p>`
    ),
    createSection(
      'creating-surveys',
      'Creating Surveys',
      `<p>If you have permission to create surveys:</p>
      <ol>
        <li>Click "Create Survey" to start the survey builder</li>
        <li>Set the title, description, and deadline</li>
        <li>Add questions using the question builder</li>
        <li>Configure audience targeting</li>
        <li>Set anonymity preferences</li>
        <li>Preview and publish</li>
      </ol>`
    ),
    createSection(
      'results',
      'Viewing Results',
      `<p>Survey results provide insights through:</p>
      <ul>
        <li><strong>Response Rate</strong> — How many targeted people responded</li>
        <li><strong>Charts</strong> — Visual breakdowns for rating and choice questions</li>
        <li><strong>Text Responses</strong> — Aggregated open-ended answers</li>
        <li><strong>Trends</strong> — Compare results across survey rounds</li>
        <li><strong>Export</strong> — Download results as CSV for external analysis</li>
      </ul>`
    ),
  ],

  walkthroughs: [
    createWalkthrough(
      'walkthrough-create',
      'How to Create a Survey',
      'Build and publish a new survey from scratch.',
      [
        {
          title: 'Start the survey builder',
          description: 'Click "Create Survey" from the Surveys page to open the builder.',
        },
        {
          title: 'Set survey details',
          description: 'Enter a clear title, description, and set the close date / deadline.',
        },
        {
          title: 'Add questions',
          description: 'Click "Add Question" and choose the question type. Write the question text and configure options.',
        },
        {
          title: 'Configure each question',
          description: 'Set whether each question is required. For choice questions, add answer options. For scales, set min/max values.',
        },
        {
          title: 'Select your audience',
          description: 'Choose who should receive the survey — all employees, specific teams, or custom selection.',
        },
        {
          title: 'Set anonymity and publish',
          description: 'Choose whether responses are anonymous. Preview the survey, then click Publish to send it out.',
        },
      ],
      5
    ),
    createWalkthrough(
      'walkthrough-results',
      'How to Analyze Survey Results',
      'Review and interpret survey responses.',
      [
        {
          title: 'Open survey results',
          description: 'Navigate to the completed survey and click "View Results".',
        },
        {
          title: 'Check response rate',
          description: 'Review the overall response rate. Higher rates mean more reliable insights.',
        },
        {
          title: 'Review by question',
          description: 'Go through each question\'s results. Charts show distributions for quantitative questions.',
        },
        {
          title: 'Export if needed',
          description: 'Click Export to download results as CSV for deeper analysis in spreadsheets.',
        },
      ],
      3
    ),
  ],

  troubleshooting: [
    createTroubleshooting(
      'trouble-not-visible',
      'Survey not appearing for targeted audience',
      [
        'Audience selection may not include the expected people',
        'Survey may not be published yet (still in draft)',
        'Survey deadline may have passed',
      ],
      [
        {
          description: 'Check survey status',
          steps: [
            'Verify the survey is published (not in draft mode)',
            'Check that the close date has not passed',
          ],
        },
        {
          description: 'Verify audience targeting',
          steps: [
            'Review the audience selection in survey settings',
            'Ensure the correct teams or individuals are included',
          ],
        },
      ]
    ),
    createTroubleshooting(
      'trouble-low-response',
      'Low survey response rates',
      [
        'Survey too long',
        'Purpose not clearly communicated',
        'No reminder sent',
        'Deadline too tight',
      ],
      [
        {
          description: 'Improve survey design',
          steps: [
            'Keep surveys under 10 minutes to complete',
            'Write a compelling description explaining why feedback matters',
            'Mix question types to keep respondents engaged',
          ],
        },
        {
          description: 'Send reminders',
          steps: [
            'Send a reminder as the deadline approaches',
            'Share results from previous surveys to show feedback was acted upon',
          ],
        },
      ]
    ),
  ],

  faqs: [
    createFAQ(
      'faq-anonymous',
      'Are my survey responses anonymous?',
      'It depends on the survey settings. Each survey clearly indicates whether responses are anonymous or attributed. Look for the anonymity notice before starting. When anonymous, even administrators cannot see individual responses.',
      ['anonymous', 'privacy', 'confidential', 'identity']
    ),
    createFAQ(
      'faq-edit-response',
      'Can I edit my response after submitting?',
      'This depends on the survey settings. Some surveys allow response editing until the deadline, while others lock responses upon submission. Check the survey details for editing policy.',
      ['edit', 'change', 'modify', 'response', 'update']
    ),
    createFAQ(
      'faq-results-access',
      'Who can see survey results?',
      'Survey creators and administrators can see aggregated results. Individual responses are only visible if the survey is not anonymous. Results may be shared with leadership at the creator\'s discretion.',
      ['results', 'visibility', 'access', 'who']
    ),
    createFAQ(
      'faq-question-types',
      'What question types are available?',
      'Available types include: Multiple Choice, Checkbox, Rating (1-5), NPS (0-10), Linear Scale, Text (open-ended), Matrix (grid), and Ranking. Each type is suited for different kinds of feedback.',
      ['types', 'questions', 'format', 'options']
    ),
    createFAQ(
      'faq-audience',
      'How do I target specific people with a survey?',
      'When creating a survey, use the Audience selector to choose: All Employees, Specific Teams, Specific Departments, or Custom selection of individual people.',
      ['audience', 'target', 'specific', 'team', 'department']
    ),
    createFAQ(
      'faq-export',
      'Can I export survey results?',
      'Yes, survey results can be exported to CSV format for analysis in spreadsheets. Look for the Export button on the results page.',
      ['export', 'download', 'CSV', 'data']
    ),
    createFAQ(
      'faq-edit-published',
      'Can I edit a survey after publishing?',
      'Minor edits (like fixing typos in descriptions) may be possible, but adding or removing questions after people have responded can skew results. It\'s best to finalize the survey before publishing.',
      ['edit', 'published', 'change', 'after']
    ),
    createFAQ(
      'faq-deadline',
      'What happens when a survey deadline passes?',
      'The survey automatically closes and no more responses can be submitted. Results become final. The creator can choose to extend the deadline before it expires.',
      ['deadline', 'close', 'expire', 'extend']
    ),
  ],

  relatedLinks: [
    createRelatedLink('Dashboard', '/', { description: 'View pending surveys on your dashboard' }),
    createRelatedLink('Analytics', '/analytics', { description: 'View survey engagement analytics' }),
    createRelatedLink('News', '/news', { description: 'Announce survey results to the organization' }),
  ],

  keywords: [
    'feedback',
    'polls',
    'questions',
    'engagement',
    'responses',
    'NPS',
    'rating',
    'anonymous',
    'survey',
    'questionnaire',
  ],
});

// =============================================================================
// SECTION-LEVEL HELP
// =============================================================================

export const surveyCreateHelp: HelpContent = createHelpContent({
  id: 'surveys:create',
  title: 'Create Survey',
  description: 'Build a new survey with the survey builder',
  parentId: 'surveys',
  sections: [
    createSection(
      'overview',
      'Overview',
      '<p>The survey builder lets you create surveys with multiple question types, audience targeting, and anonymity settings. Follow the steps to build, preview, and publish your survey.</p>'
    ),
    createSection(
      'question-builder',
      'Question Builder',
      `<p>For each question:</p>
      <ul>
        <li>Choose the question type (multiple choice, rating, text, etc.)</li>
        <li>Write clear, unambiguous question text</li>
        <li>Add answer options for choice-based questions</li>
        <li>Mark whether the question is required</li>
        <li>Drag to reorder questions</li>
      </ul>`
    ),
  ],
  faqs: [
    createFAQ('faq-1', 'How many questions should a survey have?', 'Aim for 5-15 questions. Shorter surveys get higher response rates. If you need more, consider splitting into multiple surveys.', ['length', 'count', 'how-many']),
    createFAQ('faq-2', 'Can I reuse questions from a previous survey?', 'Currently, questions need to be added individually. You can reference a previous survey as a template.', ['reuse', 'copy', 'template']),
  ],
  keywords: ['create', 'build', 'questions', 'builder', 'new survey'],
});

export const surveyResultsHelp: HelpContent = createHelpContent({
  id: 'surveys:results',
  title: 'Survey Results',
  description: 'View and analyze survey responses',
  parentId: 'surveys',
  sections: [
    createSection(
      'overview',
      'Overview',
      '<p>The results page shows aggregated responses for a completed or active survey. View response rates, charts for quantitative questions, and text responses for open-ended questions.</p>'
    ),
    createSection(
      'interpreting',
      'Interpreting Results',
      `<p>Tips for meaningful analysis:</p>
      <ul>
        <li>Consider the response rate — low rates may not represent the full audience</li>
        <li>Look for patterns in NPS scores (Promoters 9-10, Passives 7-8, Detractors 0-6)</li>
        <li>Read text responses for qualitative insights that numbers can't capture</li>
        <li>Compare with previous survey rounds to track trends</li>
      </ul>`
    ),
  ],
  faqs: [
    createFAQ('faq-1', 'What is a good response rate?', 'Above 70% is considered good. Above 50% is acceptable. Below 30% may not be representative.', ['response rate', 'good', 'target']),
    createFAQ('faq-2', 'Can I filter results by team or department?', 'If the survey was not anonymous, results can sometimes be filtered by team. Anonymous surveys only show aggregate data.', ['filter', 'team', 'segment']),
  ],
  keywords: ['results', 'responses', 'analysis', 'charts', 'data'],
});
